import * as Redux from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { uniqueId } from 'lodash';
import type { UseFormMethods } from 'react-hook-form';
import { push } from 'react-router-redux';
import { LocalStorage, arrayMove } from '~/helpers';
import { toastr } from '~/helpers/Toastr';
import type {
	InvoiceCategory,
	InvoiceSequenceData,
	InvoiceSequenceHeader,
} from '~/ogm-hub';
import { SecondarySequence } from '~/ogm-hub';
import type {
	ProductWithSupplierCategories,
	updateDraftArgs,
} from '~/Slices/InvoiceSequenceSlice';
import {
	getInvoiceSequenceById,
	getOrCreateInvoiceSequenceDraft,
	searchForProduct,
	submitInvoiceSequenceDraft,
	updateInvoiceSequenceDraft,
} from '~/Slices/InvoiceSequenceSlice';
import { LoadingStatus } from '~/Slices/schema';
import type { SupplierCategory } from '~/Slices/SupplierCategoriesSlice';
import {
	GetCategoriesByIds,
	GetSubCategoriesOfCategory,
	GetTopLevelCategoriesForSupplier,
} from '~/Slices/SupplierCategoriesSlice';
import { fetchSyscoSupplier } from '~/Slices/SuppliersSlice';
import type { IAppState } from '~/appstate';
import { exclude } from '~/utility';

// ==============================
// Types
// ==============================

export interface CmimSelectorOption {
	supplierCategoryId: number;
	children: CmimSelectorOption[];
	code: string;
	name: string;
	isOpen: boolean;
	hasSubCategories: boolean;
	isSelected: boolean;
	categoryHeader: ContainingCategoryHeader | null;
}

export interface ContainingCategoryHeader {
	kind: 'direct' | 'inherited';
	display: string;
	customerGL: string;
}

export type SequenceCategoryViewAction =
	| { kind: 'add'; what: string }
	| { kind: 'move'; what: string; from: string }
	| null;

export interface SequenceCategoryView {
	isOpen: boolean;
	clientId: string;
	objectId: number | null;
	desc1: string;
	desc2: string;
	customerGl: string;
	productCount: number;
	cmims: CmimDisplay[];
	isEditing: boolean;
	isDeleting: boolean;
	action: SequenceCategoryViewAction;
}

export interface CmimDisplay {
	supplierCategoryId: number;
	display: string;
	canBeUnassigned: boolean;
}

export interface CategoryHeaderFormFields {
	desc1: string;
	desc2: string;
	customerGl: string;
}

export interface SearchFormFields {
	searchText: string;
}

export const secondSequenceOptions = [
	{ label: 'SUPC', value: SecondarySequence.Supc },
	{ label: 'Description', value: SecondarySequence.Description },
];

export interface SequenceChartDetailsPageView {
	// Header
	pageLoadingStatus: LoadingStatus;
	name: string;
	secondarySequence: { label: string; value: SecondarySequence };
	showUnassignedCategoriesModal: boolean;

	// Left: CMIM Data
	showSearchResult: boolean;
	productSearchResult: ProductSearchResult | null;
	cmimData: CmimSelectorOption[];
	cmimDataLoadingStatus: LoadingStatus;

	// Right: Sequence
	sequenceCategoryViews: SequenceCategoryView[];
	isNewCategoryHeaderOpen: boolean;
}

type ExtendedCmimData = SupplierCategory & {
	isOpen: boolean;
	parentCategoryHeader: string | null;
	canBeUnassigned: boolean;
};

type ExtendedCategoryHeader = InvoiceCategory & {
	clientId: string;
	isEditing: boolean;
	isDeleting: boolean;
	isOpen: boolean;
};

type ExtendedProductSearchResult = ProductWithSupplierCategories & {
	clientId: string | null;
};

export interface SequenceChartDetailsPageData {
	sequence: InvoiceSequenceHeader;
	supplierId: number;
	draftId: number;
	shouldLoadCategories: boolean;
	supplierCategoryById: Record<number, ExtendedCmimData>;
	categoryHeaderByClientId: Record<string, ExtendedCategoryHeader>;
	categoryHeaderOrder: string[];
	selected: { kind: 'cmim'; id: number } | { kind: 'product' } | null;
	productSearchResult: ExtendedProductSearchResult | null;
}

export interface SequenceChartDetailsPageState {
	view: SequenceChartDetailsPageView;
	data: SequenceChartDetailsPageData;
}

// ==============================
// Initial State and Page Loading
// ==============================

export function makeInitialState(): SequenceChartDetailsPageState {
	return {
		view: initialView,
		data: initialData,
	};
}

const initialView: SequenceChartDetailsPageView = {
	name: '',
	cmimData: [],
	cmimDataLoadingStatus: LoadingStatus.idle,
	isNewCategoryHeaderOpen: false,
	pageLoadingStatus: LoadingStatus.loading,
	productSearchResult: null,
	showSearchResult: false,
	secondarySequence: secondSequenceOptions[0],
	sequenceCategoryViews: [],
	showUnassignedCategoriesModal: false,
};

const initialData: SequenceChartDetailsPageData = {
	sequence: null,
	supplierId: 0,
	draftId: null,
	shouldLoadCategories: false,
	supplierCategoryById: {},
	categoryHeaderByClientId: {},
	categoryHeaderOrder: [],
	selected: null,
	productSearchResult: null,
};

export const pageLoaded = createAsyncThunk<
	InvoiceSequenceData,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'seq-details/pageLoaded',
	async (invoiceSequenceId: number, { dispatch, rejectWithValue }) => {
		const syscoSupplierResponse = await dispatch(loadSyscoSupplier());
		if (syscoSupplierResponse == null) {
			rejectWithValue(null);
		}
		const syscoSupplierId = syscoSupplierResponse.payload as number;

		const customerId = LocalStorage.getCustomerId();
		const sequenceResponse = await dispatch(
			getInvoiceSequenceById({ customerId, sequenceId: invoiceSequenceId })
		);
		const categoryResponse = await dispatch(
			GetTopLevelCategoriesForSupplier(syscoSupplierId)
		);
		const draftResponse = await dispatch(
			getOrCreateInvoiceSequenceDraft(invoiceSequenceId)
		);

		if (
			!getOrCreateInvoiceSequenceDraft.fulfilled.match(draftResponse) ||
			!GetTopLevelCategoriesForSupplier.fulfilled.match(categoryResponse) ||
			!getInvoiceSequenceById.fulfilled.match(sequenceResponse) ||
			categoryResponse.payload == null ||
			draftResponse.payload == null
		) {
			toastr.error({ body: "Couldn't load page data, please try again." });
			rejectWithValue(null);
		} else {
			const categories = (draftResponse.payload as InvoiceSequenceData)
				.invoiceCategories;
			const supplierCategoriesOnInvoiceCategories = categories.flatMap(
				(x) => x.supplierCategoryIds
			);
			const topLevelSupplierCategories = (
				categoryResponse.payload as SupplierCategory[]
			).map((x) => x.id);
			const supplierCategoriesToGet =
				supplierCategoriesOnInvoiceCategories.filter(
					(x) => !topLevelSupplierCategories.includes(x)
				);

			const supplierCategoryResponse = await dispatch(
				GetCategoriesByIds({
					categoryIds: supplierCategoriesToGet,
					supplierId: syscoSupplierId,
				})
			); // TODO fix this

			if (!GetCategoriesByIds.fulfilled.match(supplierCategoryResponse)) {
				toastr.error({ body: "Couldn't load page data, please try again." });
				rejectWithValue(null);
			}
		}

		return draftResponse.payload as InvoiceSequenceData;
	}
);

export const loadSyscoSupplier = createAsyncThunk<
	number,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('seq-details/loadSyscoSupplier', async (_, { dispatch }) => {
	const supplierResponse = await dispatch(fetchSyscoSupplier());
	if (fetchSyscoSupplier.fulfilled.match(supplierResponse)) {
		return supplierResponse.payload.id;
	} else {
		toastr.error({ body: "Couldn't load supplier data" });
		return null;
	}
});

// ========================
// Transformations for view
// ========================

function applyNewViews(state: SequenceChartDetailsPageState) {
	state.view.cmimData = calculateSupplierCategoryViews(state.data);
	state.view.sequenceCategoryViews = calculateCategoryHeaderViews(state.data);
	state.view.productSearchResult = calculateProductSearchResultView(state.data);
}

function transformCmim(
	cmimDesc: ExtendedCmimData,
	state: SequenceChartDetailsPageData
): CmimSelectorOption {
	const childrenMatch = Object.values(state.supplierCategoryById).filter(
		(x) => x.parentId === cmimDesc.id
	);
	const children = childrenMatch
		.map((x) => transformCmim(x, state))
		.sort((a, b) => a.code.localeCompare(b.code));

	let isSelected = false;
	if (state.selected?.kind === 'cmim') {
		isSelected = state.selected.id === cmimDesc.id;
	}

	return {
		code: cmimDesc.code,
		name: cmimDesc.description,
		supplierCategoryId: cmimDesc.id,
		hasSubCategories: cmimDesc.isParent,
		children,
		isOpen: cmimDesc.isOpen,
		isSelected,
		categoryHeader: findContainingHeader(cmimDesc.id, state),
	};
}

function transformCategoryHeaderToView(
	clientId: string,
	state: SequenceChartDetailsPageData
): SequenceCategoryView {
	const ch = state.categoryHeaderByClientId[clientId];

	const cmims = ch.supplierCategoryIds
		.map((x) => getCmimDisplay(x, state.supplierCategoryById))
		.sort((a, b) => a.display.localeCompare(b.display));

	let action: SequenceCategoryViewAction = null;

	if (state.selected?.kind === 'cmim') {
		const selectedCmim = state.supplierCategoryById[state.selected.id];
		const selectedIsAssigned = !!selectedCmim.parentCategoryHeader;
		const isThisCategory = selectedCmim?.parentCategoryHeader === clientId;

		if (!selectedIsAssigned) {
			action = {
				kind: 'add',
				what: `${selectedCmim.code} ${selectedCmim.description}`,
			};
		}

		if (selectedIsAssigned && !isThisCategory) {
			const item =
				state.categoryHeaderByClientId[selectedCmim.parentCategoryHeader];
			action = {
				kind: 'move',
				what: `${selectedCmim.code} ${selectedCmim.description}`,
				from: `${item.firstDescription} ${item.secondDescription}`,
			};
		}
	}

	if (state.selected?.kind === 'product') {
		const psr = state.productSearchResult;
		const isAssigned = !!psr.clientId;
		const isThisCategory = psr.clientId === ch.clientId;

		if (!isAssigned) {
			action = { kind: 'add', what: `#${psr.supplierProductNumber}` };
		}

		if (isAssigned && !isThisCategory) {
			const source = state.categoryHeaderByClientId[psr.clientId];
			action = {
				kind: 'move',
				what: `#${psr.supplierProductNumber}`,
				from: `${source.firstDescription} ${source.secondDescription}`,
			};
		}
	}

	return {
		desc1: ch.firstDescription,
		desc2: ch.secondDescription,
		clientId: ch.clientId,
		customerGl: ch.glCode,
		objectId: ch.id,
		cmims,
		action,
		isOpen: ch.isOpen,
		isEditing: ch.isEditing,
		isDeleting: ch.isDeleting,
		productCount: ch.productNumbers.length,
	};
}

function getCmimDisplayForProduct(
	product: ProductWithSupplierCategories
): CmimDisplay {
	const cmims: { code: string; description: string }[] = [];
	const addCmim = (code: string, description: string) => {
		if (code != null) {
			cmims.push({ code, description });
		}
	};
	addCmim(
		product.supplierCategoryLevel1Code,
		product.supplierCategoryLevel1Description
	);
	addCmim(
		product.supplierCategoryLevel2Code,
		product.supplierCategoryLevel2Description
	);
	addCmim(
		product.supplierCategoryLevel3Code,
		product.supplierCategoryLevel3Description
	);
	addCmim(
		product.supplierCategoryLevel4Code,
		product.supplierCategoryLevel4Description
	);
	const display = cmims.map((x) => `${x.code} ${x.description}`).join(' > ');
	return {
		display,
		supplierCategoryId: 0,
		canBeUnassigned: false,
	};
}

function getCmimDisplay(
	supplierCategoryId: number,
	cmimIndex: Record<number, ExtendedCmimData>
): CmimDisplay {
	const arr: ExtendedCmimData[] = [];
	let current: ExtendedCmimData = cmimIndex[supplierCategoryId];
	const canBeUnassigned = current.canBeUnassigned;
	while (current) {
		arr.unshift(current);
		current = cmimIndex[current.parentId];
	}
	const display = arr.map((x) => `${x.code} ${x.description}`).join(' > ');
	return {
		display,
		supplierCategoryId,
		canBeUnassigned,
	};
}

export interface ProductSearchResult {
	productNumber: string;
	description: string;
	isSelected: boolean;
	supplierCategories: CmimDisplay;
	categoryHeader: ContainingCategoryHeader | null;
}

function calculateProductSearchResultView(
	state: SequenceChartDetailsPageData
): ProductSearchResult {
	const psr = state.productSearchResult;
	if (!psr) {
		return null;
	}

	return {
		description: psr.description,
		productNumber: psr.supplierProductNumber,
		categoryHeader: findItemContainingCategoryHeader(psr, state),
		supplierCategories: getCmimDisplayForProduct(psr),
		isSelected: state.selected?.kind === 'product',
	};
}

function calculateSupplierCategoryViews(
	state: SequenceChartDetailsPageData
): CmimSelectorOption[] {
	const options = Object.values(state.supplierCategoryById)
		.filter((x) => x.parentId === null)
		.map((x) => transformCmim(x, state))
		.sort((a, b) => a.code.localeCompare(b.code));

	return options;
}

function calculateCategoryHeaderViews(
	state: SequenceChartDetailsPageData
): SequenceCategoryView[] {
	const catHeads = state.categoryHeaderOrder.map((x) =>
		transformCategoryHeaderToView(x, state)
	);
	return catHeads;
}

// ==============================
// Helper Functions
// ==============================

function findContainingHeader(
	supplierCategoryId: number,
	state: SequenceChartDetailsPageData
): ContainingCategoryHeader | null {
	const cmimDesc = state.supplierCategoryById[supplierCategoryId];

	// base: top-level, unassigned cmim.
	if (cmimDesc.parentCategoryHeader === null && cmimDesc.parentId === null) {
		return null;
	}

	// base: it's assigned.
	if (cmimDesc.parentCategoryHeader) {
		const ch = state.categoryHeaderByClientId[cmimDesc.parentCategoryHeader];
		return {
			kind: 'direct',
			display: [ch.firstDescription, ch.secondDescription].join(' '),
			customerGL: ch.glCode,
		};
	}

	// maybe inherited?
	if (cmimDesc.parentCategoryHeader === null) {
		const parent = state.supplierCategoryById[cmimDesc.parentId];
		const parentHeader = findContainingHeader(parent.id, state);

		if (parentHeader) {
			return {
				kind: 'inherited',
				display: parentHeader.display,
				customerGL: parentHeader.customerGL,
			};
		}

		return null;
	}

	return null;
}

function findContainingHeaderForProduct(
	categoryLevel4Code: string,
	categoryLevel3Code: string,
	categoryLevel2Code: string,
	categoryLevel1Code: string,
	state: SequenceChartDetailsPageData
): ContainingCategoryHeader | null {
	const supplierCategories = Object.values(state.supplierCategoryById);
	// find the first supplier category loaded for the page
	const cmimDesc =
		supplierCategories.find((x) => x.code === categoryLevel4Code) ??
		supplierCategories.find((x) => x.code === categoryLevel3Code) ??
		supplierCategories.find((x) => x.code === categoryLevel2Code) ??
		supplierCategories.find((x) => x.code === categoryLevel1Code);
	// if none are found then it doesnt belong to any sequence categories so we dont care about it
	if (cmimDesc === undefined) {
		return null;
	}

	// base: top-level, unassigned cmim.
	if (cmimDesc.parentCategoryHeader === null && cmimDesc.parentId === null) {
		return null;
	}

	// base: it's assigned.
	if (cmimDesc.parentCategoryHeader) {
		const ch = state.categoryHeaderByClientId[cmimDesc.parentCategoryHeader];
		return {
			kind: 'direct',
			display: [ch.firstDescription, ch.secondDescription].join(' '),
			customerGL: ch.glCode,
		};
	}

	// maybe inherited?
	if (cmimDesc.parentCategoryHeader === null) {
		const parent = state.supplierCategoryById[cmimDesc.parentId];
		const parentHeader = findContainingHeader(parent.id, state);

		if (parentHeader) {
			return {
				kind: 'inherited',
				display: parentHeader.display,
				customerGL: parentHeader.customerGL,
			};
		}

		return null;
	}

	return null;
}

function findItemContainingCategoryHeader(
	item: ExtendedProductSearchResult,
	state: SequenceChartDetailsPageData
): ContainingCategoryHeader | null {
	// 1. Item number is directly assigned to a category header.
	const catHead = state.categoryHeaderByClientId[item.clientId];

	if (catHead) {
		return {
			kind: 'direct',
			display: [catHead.firstDescription, catHead.secondDescription].join(' '),
			customerGL: catHead.glCode,
		};
	}

	// 2. Item is unassigned, but it might get a value from whatever cmim it's in.
	const parent = findContainingHeaderForProduct(
		item.supplierCategoryLevel4Code,
		item.supplierCategoryLevel3Code,
		item.supplierCategoryLevel2Code,
		item.supplierCategoryLevel1Code,
		state
	);

	if (!parent) {
		return null;
	}

	parent.kind = 'inherited';
	return parent;
}

// =======================
// User Actions
// =======================

export const cmimExpanded = createAsyncThunk<
	SupplierCategory[],
	number,
	{ rejectValue: string[] | Response }
>(
	'seq-details/cmimExpanded',
	async (supplierCategoryId: number, { dispatch, rejectWithValue }) => {
		const response = await dispatch(
			GetSubCategoriesOfCategory(supplierCategoryId)
		);

		if (GetSubCategoriesOfCategory.fulfilled.match(response)) {
			return response.payload;
		}

		rejectWithValue(response.payload);
	}
);

export interface CategoryEditedArgs {
	index: number;
	form: UseFormMethods<CategoryHeaderFormFields>;
}

export const categoryEdited = createAsyncThunk<
	void,
	CategoryEditedArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'seq-details/categoryEdited',
	async (args: CategoryEditedArgs, { getState, dispatch }) => {
		const view =
			getState().SeqChartDetailsSlice.view.sequenceCategoryViews[args.index];
		args.form.reset({
			desc1: view.desc1,
			desc2: view.desc2,
			customerGl: view.customerGl,
		});
		dispatch(slice.actions.categoryEditBegan(args.index));
	}
);

const updateSequenceNumbers = (state: SequenceChartDetailsPageState) => {
	state.data.categoryHeaderOrder.forEach((clientId, index) => {
		state.data.categoryHeaderByClientId[clientId].sequenceNumber = index + 1; // TODO double check that
	});
};

export const searchSubmitted = createAsyncThunk<
	ProductWithSupplierCategories,
	SearchFormFields,
	{ rejectValue: string[] | Response }
>(
	'seq-details/searchSubmitted',
	async (args: SearchFormFields, { dispatch }) => {
		const response = await dispatch(
			searchForProduct({
				supplierId: 1,
				supplierProductNumber: args.searchText,
			})
		);

		if (searchForProduct.fulfilled.match(response)) {
			return response.payload;
		}

		return null;
	}
);

// =======================
// Actions: Save, Publish
// =======================

export const onPublishSequenceClicked = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('seq-details/onPublishSequenceClicked', async (_, { getState, dispatch }) => {
	const state = getState().SeqChartDetailsSlice;

	const unassignedTopLevels = Object.values(
		state.data.supplierCategoryById
	).filter((x) => x.parentId === null && x.parentCategoryHeader === null);

	if (unassignedTopLevels.length > 0) {
		dispatch(slice.actions.openUnassignedCategoriesModal());
	} else {
		dispatch(publishSequence());
	}
});

export const publishSequence = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('seq-details/publishSequence', async (_, { getState, dispatch }) => {
	const saveResponse = await dispatch(savedAsDraft('publish'));

	if (savedAsDraft.fulfilled.match(saveResponse)) {
		const state = getState().SeqChartDetailsSlice;

		const publishResponse = await dispatch(
			submitInvoiceSequenceDraft({
				invoiceSequenceId: state.data.sequence.id,
				invoiceDraftId: state.data.draftId,
			})
		);

		if (submitInvoiceSequenceDraft.fulfilled.match(publishResponse)) {
			toastr.success({
				title: 'Publish Successfull',
				body: 'draft was successfully published',
			});
			dispatch(push('/invoiceSequences'));
		} else {
			const errorString = (publishResponse.payload as string[]).join(', ');
			toastr.error({ title: "Couldn't publish draft", body: errorString });
		}
	}
});

export const savedAsDraft = createAsyncThunk<
	void,
	'save' | 'publish',
	{ state: IAppState; rejectValue: string[] | Response }
>('seq-details/savedAsDraft', async (args: string, { getState, dispatch }) => {
	const details = getState().SeqChartDetailsSlice;
	const categoryHeaders = details.data.categoryHeaderOrder.map(
		(x) => details.data.categoryHeaderByClientId[x]
	);

	const apiArgs: updateDraftArgs = {
		invoiceSequenceId: details.data.sequence.id,
		invoiceSequenceDraftId: details.data.draftId,
		args: {
			data: {
				id: details.data.draftId,
				secondarySequence: details.view.secondarySequence.value,
				invoiceCategories: categoryHeaders,
			},
		},
	};

	const res = await dispatch(updateInvoiceSequenceDraft(apiArgs));

	if (updateInvoiceSequenceDraft.rejected.match(res)) {
		const errorString = (res.payload as string[]).join(', ');
		toastr.error({ title: "Couldn't save draft", body: errorString });
		return;
	}

	if (args === 'save') {
		toastr.success({
			title: 'Save Successfull',
			body: 'draft was successfully saved!',
		});
		dispatch(push('/invoiceSequences'));
	}
});

export const slice = Redux.createSlice({
	name: 'seq-details',
	initialState: makeInitialState(),
	reducers: {
		pageLoadedFailure(state) {
			state.view.pageLoadingStatus = LoadingStatus.failure;
		},
		pageUnloaded() {
			return makeInitialState();
		},
		openUnassignedCategoriesModal(state) {
			state.view.showUnassignedCategoriesModal = true;
		},
		// =======================
		// LEFT: CMIM Selector
		// =======================
		productSearchResultSelected(state) {
			state.data.selected = { kind: 'product' };
			applyNewViews(state);
		},
		productSearchResultCategoryHeaderRemoved(state) {
			const psr = state.data.productSearchResult;

			const categoryHeader = state.data.categoryHeaderByClientId[psr.clientId];

			categoryHeader.productNumbers = exclude(
				categoryHeader.productNumbers,
				psr.supplierProductNumber
			);

			state.data.productSearchResult.clientId = null;
			applyNewViews(state);
		},
		cmimSelected(state, action: Redux.PayloadAction<number>) {
			const item = state.data.supplierCategoryById[action.payload];
			state.data.selected = { kind: 'cmim', id: item.id };
			applyNewViews(state);
		},
		// =======================
		// RIGHT: Category Headers
		// =======================
		addCategoryClicked(state) {
			state.view.isNewCategoryHeaderOpen = true;
		},
		categoryDeleted(state, action: Redux.PayloadAction<string>) {
			state.data.categoryHeaderByClientId[action.payload].isDeleting = true;
			state.view.sequenceCategoryViews = calculateCategoryHeaderViews(
				state.data
			);
		},
		categoryDeleteConfirmed(state, action: Redux.PayloadAction<string>) {
			state.data.categoryHeaderOrder = exclude(
				state.data.categoryHeaderOrder,
				action.payload
			);

			const toUnassign = Object.values(state.data.supplierCategoryById).filter(
				(x) => x.parentCategoryHeader === action.payload
			);

			for (const supplierCategory of toUnassign) {
				supplierCategory.parentCategoryHeader = null;
			}
			delete state.data.categoryHeaderByClientId[action.payload];

			applyNewViews(state);
		},
		categoryDeleteCancelled(state, action: Redux.PayloadAction<string>) {
			state.data.categoryHeaderByClientId[action.payload].isDeleting = false;
			state.view.sequenceCategoryViews = calculateCategoryHeaderViews(
				state.data
			);
		},
		cmimRemoved(
			state,
			action: Redux.PayloadAction<{
				clientId: string;
				supplierCategoryId: number;
			}>
		) {
			const item = state.data.categoryHeaderByClientId[action.payload.clientId];

			item.supplierCategoryIds = exclude(
				item.supplierCategoryIds,
				action.payload.supplierCategoryId
			);

			const cmim =
				state.data.supplierCategoryById[action.payload.supplierCategoryId];
			cmim.parentCategoryHeader = null;

			applyNewViews(state);
		},
		categorySortEnded(
			state,
			action: Redux.PayloadAction<{ newIndex: number; oldIndex: number }>
		) {
			state.data.categoryHeaderOrder = arrayMove(
				state.data.categoryHeaderOrder,
				action.payload.oldIndex,
				action.payload.newIndex
			);
			updateSequenceNumbers(state);

			applyNewViews(state);
		},
		categoryEditBegan(state, action: Redux.PayloadAction<number>) {
			const clientId = state.data.categoryHeaderOrder[action.payload];
			const item = state.data.categoryHeaderByClientId[clientId];
			item.isEditing = true;
			applyNewViews(state);
		},
		categoryEditSubmitted(
			state,
			action: Redux.PayloadAction<{
				index: number;
				values: CategoryHeaderFormFields;
			}>
		) {
			const itemId = state.data.categoryHeaderOrder[action.payload.index];
			const item = state.data.categoryHeaderByClientId[itemId];
			item.firstDescription = action.payload.values.desc1;
			item.secondDescription = action.payload.values.desc2;
			item.glCode = action.payload.values.customerGl;
			item.isEditing = false;
			applyNewViews(state);
		},
		categoryEditCancelled(state, action: Redux.PayloadAction<number>) {
			const clientId = state.data.categoryHeaderOrder[action.payload];
			const ch = state.data.categoryHeaderByClientId[clientId];
			ch.isEditing = false;
			applyNewViews(state);
		},
		moveSelectedClicked(state, action: Redux.PayloadAction<string>) {
			if (!state.data.selected) {
				return;
			}

			const clientId = action.payload;

			const categoryHeader = state.data.categoryHeaderByClientId[clientId];

			if (state.data.selected.kind === 'cmim') {
				const selectedId = state.data.selected.id;
				for (const item of Object.values(state.data.categoryHeaderByClientId)) {
					item.supplierCategoryIds = exclude(
						item.supplierCategoryIds,
						selectedId
					);
				}

				categoryHeader.supplierCategoryIds.push(selectedId);
				state.data.supplierCategoryById[selectedId].parentCategoryHeader =
					categoryHeader.clientId;
			}

			if (state.data.selected.kind === 'product') {
				const psr = state.data.productSearchResult;
				const oldCh = state.data.categoryHeaderByClientId[psr.clientId];

				if (oldCh) {
					oldCh.productNumbers = exclude(
						oldCh.productNumbers,
						psr.supplierProductNumber
					);
				}

				categoryHeader.productNumbers.push(psr.supplierProductNumber);

				psr.clientId = clientId;
			}

			applyNewViews(state);
		},
		unassignedCategoriesModalCanceled(state) {
			state.view.showUnassignedCategoriesModal = false;
		},
		newCategoryHeaderAdded(
			state,
			action: Redux.PayloadAction<CategoryHeaderFormFields>
		) {
			const clientId = uniqueId('client-');
			const newView: ExtendedCategoryHeader = {
				clientId,
				supplierCategoryIds: [],
				firstDescription: action.payload.desc1,
				secondDescription: action.payload.desc2,
				glCode: action.payload.customerGl,
				isEditing: false,
				isDeleting: false,
				isOpen: true,
				id: null,
				productNumbers: [],
				sequenceNumber: 0,
			};
			state.data.categoryHeaderByClientId[clientId] = newView;
			state.data.categoryHeaderOrder.unshift(clientId);
			updateSequenceNumbers(state);

			state.view.isNewCategoryHeaderOpen = false;
			applyNewViews(state);
		},
		newCategoryHeaderCancelled(state) {
			state.view.isNewCategoryHeaderOpen = false;
		},
		categoryHeaderClicked(state, action: Redux.PayloadAction<number>) {
			const category = state.view.sequenceCategoryViews[action.payload];
			category.isOpen = !category.isOpen;
		},
		secondarySequenceChanged(
			state,
			action: Redux.PayloadAction<{ label: string; value: SecondarySequence }>
		) {
			state.view.secondarySequence = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getInvoiceSequenceById.fulfilled, (state, action) => {
			state.data.sequence = action.payload;
		});
		builder.addCase(pageLoaded.pending, (state) => {
			state.view.pageLoadingStatus = LoadingStatus.loading;
		});
		builder.addCase(pageLoaded.fulfilled, (state, action) => {
			state.data.draftId = action.payload.id;
			state.view.pageLoadingStatus = LoadingStatus.success;
			state.view.cmimDataLoadingStatus = LoadingStatus.success;
			state.view.name = state.data.sequence.chartName;
			state.view.secondarySequence = secondSequenceOptions.find(
				(x) => x.value === action.payload.secondarySequence
			);

			const clientIdHeaders = action.payload.invoiceCategories.map((x) => {
				const clientId = uniqueId('client-');
				return {
					clientId,
					...x,
				};
			});

			state.data.categoryHeaderOrder = clientIdHeaders
				.sort((x, y) => x.sequenceNumber - y.sequenceNumber)
				.map((x) => x.clientId);

			for (const item of clientIdHeaders) {
				state.data.categoryHeaderByClientId[item.clientId] = {
					...item,
					isEditing: false,
					isDeleting: false,
					isOpen: true,
				};

				for (const c of item.supplierCategoryIds) {
					state.data.supplierCategoryById[c].parentCategoryHeader =
						item.clientId;
					state.data.supplierCategoryById[c].canBeUnassigned =
						state.data.supplierCategoryById[c].parentId != null;
				}
			}
			applyNewViews(state);
		});
		builder.addCase(pageLoaded.rejected, (state) => {
			state.view.pageLoadingStatus = LoadingStatus.failure;
			state.view.cmimDataLoadingStatus = LoadingStatus.failure;
		});
		builder.addCase(loadSyscoSupplier.fulfilled, (state, action) => {
			state.data.supplierId = action.payload;
		});
		builder.addCase(
			GetTopLevelCategoriesForSupplier.fulfilled,
			(state, action) => {
				for (const item of action.payload) {
					state.data.supplierCategoryById[item.id] = {
						...item,
						isOpen: false,
						parentCategoryHeader: null,
						canBeUnassigned: true,
					};
				}
				applyNewViews(state);
			}
		);
		builder.addCase(GetCategoriesByIds.fulfilled, (state, action) => {
			for (const item of action.payload) {
				state.data.supplierCategoryById[item.id] = {
					...item,
					isOpen: false,
					parentCategoryHeader: null,
					canBeUnassigned: false,
				};
			}
			applyNewViews(state);
		});
		builder.addCase(searchSubmitted.fulfilled, (state, action) => {
			state.view.cmimDataLoadingStatus = LoadingStatus.success;
			state.view.showSearchResult = true;

			if (action.payload != null) {
				const source = Object.values(state.data.categoryHeaderByClientId)
					.filter((x) => !!x.id)
					.find((x) =>
						x.productNumbers.includes(action.payload.supplierProductNumber)
					);
				const clientId = source?.clientId;

				state.data.productSearchResult = { ...action.payload, clientId };
			} else {
				state.data.productSearchResult = null;
			}

			applyNewViews(state);
		});
		builder.addCase(searchSubmitted.pending, (state) => {
			state.view.cmimDataLoadingStatus = LoadingStatus.loading;
			applyNewViews(state);
		});
		builder.addCase(cmimExpanded.pending, (state, action) => {
			const item = state.data.supplierCategoryById[action.meta.arg];

			if (item.isParent) {
				if (!item.isOpen) {
					state.data.shouldLoadCategories = true;
					state.view.cmimDataLoadingStatus = LoadingStatus.loading;
				}

				item.isOpen = !item.isOpen;
			}
		});
		builder.addCase(cmimExpanded.fulfilled, (state, action) => {
			state.data.shouldLoadCategories = false;
			for (const item of action.payload) {
				if (state.data.supplierCategoryById[item.id] == null) {
					state.data.supplierCategoryById[item.id] = {
						...item,
						isOpen: false,
						parentCategoryHeader: null,
						canBeUnassigned: true,
					};
				}
			}

			applyNewViews(state);
			state.view.cmimDataLoadingStatus = LoadingStatus.success;
		});
	},
});

const asyncActions = {
	pageLoaded,
	onPublishSequenceClicked,
	publishSequence,
	searchSubmitted,
	categoryEdited,
	cmimExpanded,
	savedAsDraft,
};

export const dispatchProps = { ...slice.actions, ...asyncActions };
